import { Button as AntButton, DatePicker, Modal, Select } from "antd";
import _ from "lodash";
import moment from "moment";
import { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { createPaymentItem } from "../../../../api/payments.api";
import { getStudentMonths } from "../../../../api/semesters.api";
import { getStudentClasses } from "../../../../api/studentsClass.api";
import useAxios from "../../../../hooks/useAxios";
import useNotification from "../../../../hooks/useNotification";
import { getCommonCodeLabel } from "../../../../lib/store";
import { IGetStudentMonths } from "../../../../models/semesters.model";
import { IGetStudentClasses, IStudentClass } from "../../../../models/studentsClass.model";
import ErrorResponse from "../../../ErrorResponse";
import Loader from "../../../Loader";
import { IPaymentItemReq } from '../../../../models/payments.model';
import { PAYMENT_TARGET_TYPE } from '../../../../constans';

const Block = styled.div`
  th {
    text-align: center !important;
    vertical-align: middle;
  }
`;


interface IProps {
  visible: boolean;
  setVisible: (visible: boolean) => void;
  memberCode: string|undefined;
  doGetStudentPayments: () => void;
  sendLog?: (message: string, object?: object) => void;
};

const AddPaymentModal: FC<IProps> = ({
  visible,
  setVisible,
  memberCode,
  doGetStudentPayments,
  sendLog,
}) => {
  const { t, i18n } = useTranslation();
  const { infoNotification, successNotification } = useNotification();
  const { data: res, isLoading, error, execute } = useAxios<IGetStudentMonths>();
  const { data: res2, isLoading: isLoading2, error: error2, execute: execute2 } = useAxios<IGetStudentClasses>();
  const { data: submitRes, isLoading: submitIsLoading, error: submitError, execute: submitExecute } = useAxios();

  const [type, setType] = useState(PAYMENT_TARGET_TYPE.Shuttle);
  const [classStudentCode, setClassStudentCode] = useState<string>();
  const [classes, setClasses] = useState<IStudentClass[]>([]);
  const [shuttleFees, setShuttleFees] = useState<(number|undefined)[]>([undefined, undefined, undefined]);
  const [specialClassFees, setSpecialClassFees] = useState<(number|undefined)[]>([undefined, undefined, undefined]);
  const [specialClassEtc, setSpecialClassEtc] = useState<(number|undefined)[]>([]);
  const [price, setPrice] = useState<number>();
  const [memo, setMemo] = useState('');
  const [paymentCloseDate, setPaymentCloseDate] = useState(moment().add(7, 'days').format('YYYYMMDD'));
  const [semesterType, setSemesterType] = useState<string>();
  const [selClassCode, setSelClassCode] = useState<string>();

  const PAYMENT_TYPE_NAME: { [key in PAYMENT_TARGET_TYPE]: string } = {
    [PAYMENT_TARGET_TYPE.Shuttle]: 'student.student.option18',
    [PAYMENT_TARGET_TYPE.SpecialClass]: 'student.student.option35',
    [PAYMENT_TARGET_TYPE.Default]: 'student.student.option19'
  }

  const createPaymentReq = (
    paymentTargetCode: string,
    classStudentCode: number,
    monthSeq?: number,
    price?: number,
    paymentCloseDate?: string,
    memo?: string
  ) => ({
    paymentTargetCode,
    classStudentCode,
    monthSeq,
    price,
    paymentCloseDate,
    memo,
  } as unknown as IPaymentItemReq);

  const handleShuttleSubmit: () => void = () => {
    if (!_.sum(shuttleFees)) {
      return infoNotification(t('student.student.message27'));
    }
    const req = shuttleFees.map((fee, index) =>
      createPaymentReq(PAYMENT_TARGET_TYPE.Shuttle, Number(classStudentCode), index + 1, fee))
      .filter(item => item.price);

    submitExecute(createPaymentItem(req));
    sendLog?.('createPaymentItem', req);
  };

  const handleSpecialClassSubmit: () => void = () => {
    if (!_.sum(specialClassFees)) {
      return infoNotification(t('student.student.message59'));
    }
    const req = specialClassFees?.map((fee, index) =>
      createPaymentReq(PAYMENT_TARGET_TYPE.SpecialClass, Number(classStudentCode), index + 1, fee))
      .filter(item => item.price);

    submitExecute(createPaymentItem(req));
    sendLog?.('createPaymentItem', req);
  };

  const handleDefaultSubmit: () => void = () => {
    if(semesterType === 'SC') {
      //기타 + 특강학기 일 때 처리
      if (!_.sum(specialClassEtc)) {
        return infoNotification(t('student.student.message28'));
      }
      const req = specialClassEtc?.map((fee, index) =>
        createPaymentReq(PAYMENT_TARGET_TYPE.Default, Number(classStudentCode), index + 1, fee))
        .filter(item => item.price);
  
      submitExecute(createPaymentItem(req));
      sendLog?.('createPaymentItem', req);
    } else {
      //기타 + 정규학기 일 때 처리
      if (!price) {
        return infoNotification(t('student.student.message28'));
      }
      if (!paymentCloseDate) {
        return infoNotification(t('student.student.message29'));
      }
      const req = Array.of(createPaymentReq(PAYMENT_TARGET_TYPE.Default, Number(classStudentCode), undefined, price, paymentCloseDate, memo));

      submitExecute(createPaymentItem(req));
      sendLog?.('createPaymentItem', req);
    }
  };

  const submitActionMap: { [key in PAYMENT_TARGET_TYPE]: () => void } = {
    [PAYMENT_TARGET_TYPE.Shuttle]: handleShuttleSubmit,
    [PAYMENT_TARGET_TYPE.SpecialClass]: handleSpecialClassSubmit,
    [PAYMENT_TARGET_TYPE.Default]: handleDefaultSubmit
  };

  const onSubmit = () => {
    if (!classStudentCode) {
      return infoNotification(t('student.student.message13'));
    }

    const action = submitActionMap[type || PAYMENT_TARGET_TYPE.Default];
    action();
  }

  useEffect(() => {
    if (memberCode) {
      execute(getStudentMonths(memberCode));
      execute2(getStudentClasses(memberCode));
    }
  }, [memberCode]);

  useEffect(() => {
    if (res2) {
      const newClasses = res2.data?.filter(item => item.studyStatus === 'RE' || item.studyStatus === 'PL');
      setClasses(newClasses);
      const currentClass = newClasses?.find(item => item.studyStatus === 'RE');
      //특강 + 셔틀비일 때는 수강반 셋팅X
      if(!(type === PAYMENT_TARGET_TYPE.Shuttle && currentClass?.semesterType === 'SC')) {
        setClassStudentCode(currentClass?.classStudentCode);
      }
      setSemesterType(currentClass?.semesterType);
      setSelClassCode(currentClass?.classCode);
    }
  }, [res2]);

  useEffect(() => {
    if (submitRes) {
      doGetStudentPayments();
      successNotification(t('student.student.message26'));
      onClose();
    }
  }, [submitRes]);

  const getMonthNameCode = (monthSeq: number) => {
    const currentClass = classes?.find(item => item.classStudentCode === classStudentCode);
    return res?.data?.find(item => item.monthSeq === monthSeq && item.classCode === currentClass?.classCode)?.monthNameCode?.trim() || '';
  };

  const onClose = () => {
    setType(PAYMENT_TARGET_TYPE.Shuttle);
    setShuttleFees([undefined, undefined, undefined]);
    setSpecialClassFees([undefined, undefined, undefined]);
    setPrice(undefined);
    setMemo('');
    setPaymentCloseDate(moment().add(7, 'days').format('YYYYMMDD'));
    setVisible(false);
  };

  const onSetShuttleFees = (index: number, value: string) => {
    if (isNaN(Number(value))) {
      return;
    }
    const newShuttleFees = [...shuttleFees];
    newShuttleFees[index] = value ? Number(value) : undefined;
    setShuttleFees(newShuttleFees);
  }

  const onSetSpecialClassFees = (index: number, value: string) => {
    if (isNaN(Number(value))) {
      return;
    }
    const newSpecialClassFees = [...specialClassFees];
    newSpecialClassFees[index] = value ? Number(value) : undefined;
    setSpecialClassFees(newSpecialClassFees);
  }

  const onSetSpecialClassEtc = (index: number, value: string) => {
    if (isNaN(Number(value))) {
      return;
    }
    const newSpecialClassEtc = [...specialClassEtc];
    newSpecialClassEtc[index] = value ? Number(value) : undefined;
    setSpecialClassEtc(newSpecialClassEtc);
  }

  return (
    <>
      {(isLoading || isLoading2 || submitIsLoading) && <Loader />}
      <ErrorResponse error={error} />
      <ErrorResponse error={error2} />
      <ErrorResponse error={submitError} />

      <Modal
        title={t('student.student.text9')}
        width={400}
        visible={visible}
        onCancel={onClose}
        footer={[
          <div className='comm-btn__area justify-content-center'>
            <AntButton
              key="submit"
              className='btn-basic pink'
              onClick={onSubmit}
            >
              {t('common.button.add')}
            </AntButton>
            <AntButton
              key="cancel"
              className='btn-basic gray'
              onClick={onClose}
            >
              {t('common.button.cancel')}
            </AntButton>
          </div>
        ]}
      >
        <Block className='comm-table__area rowType edit mt-0'>
          <table>
              {
                ([PAYMENT_TARGET_TYPE.Shuttle, PAYMENT_TARGET_TYPE.SpecialClass].includes(type) || (PAYMENT_TARGET_TYPE.Default && semesterType === 'SC') )
                  ? (
                    <colgroup>
                      <col width="100px" />
                      <col width="100px" />
                      <col width="*" />
                    </colgroup>
                    )
                  : (
                    <colgroup>
                      <col width="100px" />
                      <col width="*" />
                    </colgroup>
                    )
              }
            <tbody>
              <tr>
                <th>{t('student.student.column45')}</th>
                <td colSpan={2}>
                  <div className="w-150p mr-2">
                    <Select
                      className="w-100"
                      value={type}
                      onChange={value => {
                        setType(value);
                        if(value === PAYMENT_TARGET_TYPE.Shuttle && semesterType === 'SC') {
                          //셔틀선택 시 학급이 특강반이면 학급 초기화
                          setClassStudentCode(undefined);
                        }
                      }}
                    >
                      {
                        Object.values(PAYMENT_TARGET_TYPE).map(value => {
                          if(value !== PAYMENT_TARGET_TYPE.SpecialClass)
                            return (<Select.Option value={value}>{t(PAYMENT_TYPE_NAME[value])}</Select.Option>)
                        })
                      }
                    </Select>
                  </div>
                </td>
              </tr>
              <tr>
                <th>{t('student.student.column23')}</th>
                <td colSpan={2}>
                  <div className="w-150p mr-2">
                    <Select
                      className="w-100"
                      value={classStudentCode}
                      onChange={(value, item) => {
                        setClassStudentCode(value);
                        
                        Object.entries(item).map(([key, value])=>{
                          if(key === 'semesterType') {
                            setSemesterType(value);
                          }

                          if(key === 'classCode') {
                            setSelClassCode(value);
                          }
                        });
                      }}
                    >
                    {classes.map((item, index) => (
                      !(type === PAYMENT_TARGET_TYPE.Shuttle && item.semesterType === 'SC') &&
                      <Select.Option
                        key={index}
                        value={item.classStudentCode}
                        semesterType={item.semesterType}
                        classCode={item.classCode}
                      >
                        {item.className}
                      </Select.Option>
                    ))}
                    </Select>
                  </div>
                </td>
              </tr>
              {
                (
                  () => {
                    if(type === PAYMENT_TARGET_TYPE.Shuttle) {
                      return (
                        <>
                          <tr>
                            <th rowSpan={3}>{t('student.student.column46')}</th>
                            {/* @ts-ignore */}
                            <th>{getCommonCodeLabel('SY0009', getMonthNameCode(1), i18n.language)}</th>
                            <td>
                              <input
                                type="text"
                                className="form-control"
                                value={shuttleFees[0] || ''}
                                onChange={e => onSetShuttleFees(0, e.target.value)}
                              />
                            </td>
                          </tr>
                          <tr>
                            {/* @ts-ignore */}
                            <th>{getCommonCodeLabel('SY0009', getMonthNameCode(2), i18n.language)}</th>
                            <td>
                              <input
                                type="text"
                                className="form-control"
                                value={shuttleFees[1]}
                                onChange={e => onSetShuttleFees(1, e.target.value)}
                              />
                            </td>
                          </tr>
                          <tr>
                            {/* @ts-ignore */}
                            <th>{getCommonCodeLabel('SY0009', getMonthNameCode(3), i18n.language)}</th>
                            <td>
                              <input
                                type="text"
                                className="form-control"
                                value={shuttleFees[2]}
                                onChange={e => onSetShuttleFees(2, e.target.value)}
                              />
                            </td>
                          </tr>
                          <tr>
                            <th>{t('student.student.column47')}</th>
                            <td colSpan={2}>
                              {_.sum(shuttleFees)}
                            </td>
                          </tr>
                        </>
                      );
                    } else if(type === PAYMENT_TARGET_TYPE.SpecialClass) {
                      return (
                        <>
                          <tr>
                            <th rowSpan={3}>{t('student.student.column46')}</th>
                            {/* @ts-ignore */}
                            <th>{getCommonCodeLabel('SY0009', getMonthNameCode(1), i18n.language)}</th>
                            <td>
                              <input
                                type="text"
                                className="form-control"
                                value={specialClassFees[0] || ''}
                                onChange={e => onSetSpecialClassFees(0, e.target.value)}
                              />
                            </td>
                          </tr>
                          <tr>
                            {/* @ts-ignore */}
                            <th>{getCommonCodeLabel('SY0009', getMonthNameCode(2), i18n.language)}</th>
                            <td>
                              <input
                                type="text"
                                className="form-control"
                                value={specialClassFees[1]}
                                onChange={e => onSetSpecialClassFees(1, e.target.value)}
                              />
                            </td>
                          </tr>
                          <tr>
                            {/* @ts-ignore */}
                            <th>{getCommonCodeLabel('SY0009', getMonthNameCode(3), i18n.language)}</th>
                            <td>
                              <input
                                type="text"
                                className="form-control"
                                value={specialClassFees[2]}
                                onChange={e => onSetSpecialClassFees(2, e.target.value)}
                              />
                            </td>
                          </tr>
                          <tr>
                            <th>{t('student.student.column47')}</th>
                            <td colSpan={2}>
                              {_.sum(specialClassFees)}
                            </td>
                          </tr>
                        </>
                      );
                    } else if(type === PAYMENT_TARGET_TYPE.Default && semesterType === 'SC' ) {
                      return (
                        <>
                          {res?.data?.filter((item)=>item.classCode === selClassCode).map((vItem, index)=>{
                            return (
                              <tr>
                                {(index === 0) && <th rowSpan={res?.data?.filter((item)=>item.classCode === selClassCode).length}>{t('student.student.column46')}</th>}
                                {/* @ts-ignore */}
                                <th>{getCommonCodeLabel('SY0009', getMonthNameCode(vItem.monthSeq), i18n.language)}</th>
                                <td>
                                  <input
                                    type="text"
                                    className="form-control"
                                    value={specialClassEtc[index] || ''}
                                    onChange={e => onSetSpecialClassEtc(index, e.target.value)}
                                  />
                                </td>
                              </tr>
                            );
                          })}
                          <tr>
                            <th>{t('student.student.column47')}</th>
                            <td colSpan={2}>
                              {_.sum(specialClassEtc)}
                            </td>
                          </tr>
                        </>
                      );
                    } else {
                      return (
                        <>
                          <tr>
                            <th>{t('student.student.column46')}</th>
                            <td colSpan={2}>
                              <input
                                type="number"
                                className="form-control"
                                value={price}
                                onChange={e => setPrice(Number(e.target.value))}
                              />
                            </td>
                          </tr>
                          <tr>
                            <th>{t('student.student.column73')}</th>
                            <td colSpan={2}>
                              <input
                                type="text"
                                className="form-control"
                                value={memo}
                                onChange={e => setMemo(e.target.value)}
                              />
                            </td>
                          </tr>
                          <tr>
                            <th>{t('student.student.column74')}</th>
                            <td colSpan={2} style={{ textAlign: 'left' }}>
                              <div className="date-picker__area">
                                <DatePicker
                                  value={paymentCloseDate ? moment(paymentCloseDate) : undefined}
                                  onChange={(e) => {
                                    if (e === null) setPaymentCloseDate('');
                                    else setPaymentCloseDate(moment(e).format('YYYYMMDD'));
                                  }}
                                />
                              </div>
                            </td>
                          </tr>
                        </>
                      )
                    }
                  }
                )()
              }
            </tbody>
          </table>
        </Block>
      </Modal>
    </>
  );
}

export default AddPaymentModal;